// Phase 2 of the superadmin build: platform-wide pricing config (not
// tenant data -- see migration 0098). Three independent pieces:
//   1. Per-module graduated rate, split into two tracks (migration 0105):
//      'base' tiers apply to whichever module a tenant enabled FIRST,
//      'addon' tiers apply to every module they add after that. A tenant
//      with only one module always pays its base rate; the addon rate
//      only ever applies to their 2nd+ module.
//   2. A volume discount on the combined total, keyed on how many
//      modules a tenant has enabled -- stacks on top of #1 rather than
//      replacing it, so a tenant with many modules gets both the
//      cheaper addon rates AND the volume discount.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { Plus, Trash2, Tag, Percent, SlidersHorizontal } from 'lucide-react';
import { moduleIcon } from '@/lib/moduleIcons';
import { logPlatformAdminAction } from '@/lib/platformAuditLog';
import { CORE_MODULE_KEYS } from '@/lib/coreModules';

interface ModuleRow {
  module_key: string;
  module_name: string;
  billing_basis: 'per_vehicle' | 'per_user' | 'flat';
  excluded_from_module_discount: boolean;
}

interface TierRow {
  id: string;
  module_key: string;
  rate_type: 'base' | 'addon';
  tier_min: number;
  tier_max: number | null;
  unit_price: number;
}

interface DiscountTierRow {
  id: string;
  min_modules: number;
  max_modules: number | null;
  discount_percent: number;
}

const basisLabel: Record<ModuleRow['billing_basis'], string> = {
  per_vehicle: 'per vehicle',
  per_user: 'per user',
  flat: 'flat',
};

const basisUnit: Record<ModuleRow['billing_basis'], string> = {
  per_vehicle: '/vehicle',
  per_user: '/user',
  flat: '/month',
};

const lowestTier = (tiers: TierRow[]): TierRow | null =>
  tiers.length ? tiers.reduce((lowest, t) => (t.tier_min < lowest.tier_min ? t : lowest), tiers[0]) : null;

// One rate track's tier list + add button -- rendered twice per module
// popover (once for 'base', once for 'addon') so the two tracks can't
// drift out of sync visually.
const TierEditor: React.FC<{
  tiers: TierRow[];
  basis: ModuleRow['billing_basis'];
  onUpdate: (tier: TierRow, field: 'tier_min' | 'tier_max' | 'unit_price', value: string) => void;
  onDelete: (tier: TierRow) => void;
  onAdd: () => void;
}> = ({ tiers, basis, onUpdate, onDelete, onAdd }) => (
  <div className="space-y-1.5">
    {tiers.map((t) => (
      <div key={t.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] text-muted-foreground">{basis === 'flat' ? 'From (n/a)' : 'From'}</Label>
          <Input
            type="number"
            value={t.tier_min}
            onChange={(e) => onUpdate(t, 'tier_min', e.target.value)}
            className="dark-input h-8 text-xs"
            disabled={basis === 'flat'}
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">To (blank = ∞)</Label>
          <Input
            type="number"
            value={t.tier_max ?? ''}
            placeholder="∞"
            onChange={(e) => onUpdate(t, 'tier_max', e.target.value)}
            className="dark-input h-8 text-xs"
            disabled={basis === 'flat'}
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">{basis === 'flat' ? 'Price ($)' : 'Unit price ($)'}</Label>
          <Input
            type="number"
            step="0.01"
            value={t.unit_price}
            onChange={(e) => onUpdate(t, 'unit_price', e.target.value)}
            className="dark-input h-8 text-xs"
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => onDelete(t)} className="text-red-400 hover:text-red-300 h-8">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    ))}
    <Button
      size="sm"
      variant="outline"
      onClick={onAdd}
      disabled={basis === 'flat' && tiers.length >= 1}
      className="border-yellow-600 text-yellow-400 hover:bg-yellow-900 h-7 text-xs"
    >
      <Plus className="w-3 h-3 mr-1" />
      {basis === 'flat' ? 'Set flat price' : 'Add tier'}
    </Button>
  </div>
);

const SuperadminPricing: React.FC = () => {
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [tiersByModule, setTiersByModule] = useState<Record<string, TierRow[]>>({});
  const [discountTiers, setDiscountTiers] = useState<DiscountTierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingBasisKey, setSavingBasisKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: moduleRows, error: moduleErr }, { data: tierRows, error: tierErr }, { data: discountRows, error: discountErr }] =
      await Promise.all([
        supabase
          .from('app_modules')
          .select('module_key, module_name, billing_basis, excluded_from_module_discount')
          .eq('is_active', true)
          .order('sort_order'),
        supabase.from('pricing_module_tiers').select('*').order('tier_min'),
        supabase.from('pricing_module_count_discounts').select('*').order('min_modules'),
      ]);
    if (moduleErr) toast({ title: 'Error', description: 'Failed to load modules: ' + moduleErr.message, variant: 'destructive' });
    if (tierErr) toast({ title: 'Error', description: 'Failed to load rate tiers: ' + tierErr.message, variant: 'destructive' });
    if (discountErr) toast({ title: 'Error', description: 'Failed to load discount tiers: ' + discountErr.message, variant: 'destructive' });

    // Dashboard & Settings are base-app, not paid modules -- never priced,
    // never shown here (see coreModules.ts).
    setModules(((moduleRows as ModuleRow[]) || []).filter((m) => !CORE_MODULE_KEYS.includes(m.module_key)));
    const grouped: Record<string, TierRow[]> = {};
    ((tierRows as TierRow[]) || []).forEach((t) => {
      (grouped[t.module_key] ??= []).push(t);
    });
    setTiersByModule(grouped);
    setDiscountTiers((discountRows as DiscountTierRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const updateBasis = async (moduleKey: string, basis: ModuleRow['billing_basis']) => {
    setSavingBasisKey(moduleKey);
    setModules((prev) => prev.map((m) => (m.module_key === moduleKey ? { ...m, billing_basis: basis } : m)));
    const { error } = await supabase.from('app_modules').update({ billing_basis: basis }).eq('module_key', moduleKey);
    setSavingBasisKey(null);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update billing basis: ' + error.message, variant: 'destructive' });
      load();
      return;
    }
    logPlatformAdminAction('pricing_basis_changed', 'app_module', moduleKey, { basis });
  };

  const toggleExcludedFromDiscount = async (moduleKey: string, excluded: boolean) => {
    setSavingBasisKey(moduleKey);
    setModules((prev) => prev.map((m) => (m.module_key === moduleKey ? { ...m, excluded_from_module_discount: excluded } : m)));
    const { error } = await supabase
      .from('app_modules')
      .update({ excluded_from_module_discount: excluded })
      .eq('module_key', moduleKey);
    setSavingBasisKey(null);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
      load();
      return;
    }
    logPlatformAdminAction('pricing_module_discount_exclusion_changed', 'app_module', moduleKey, { excluded });
  };

  const addTier = async (moduleKey: string, rateType: TierRow['rate_type']) => {
    const existing = (tiersByModule[moduleKey] || []).filter((t) => t.rate_type === rateType);
    const nextMin = existing.length ? Math.max(...existing.map((t) => t.tier_max ?? t.tier_min)) + 1 : 1;
    const { data, error } = await supabase
      .from('pricing_module_tiers')
      .insert({ module_key: moduleKey, rate_type: rateType, tier_min: nextMin, tier_max: null, unit_price: 0 })
      .select('*')
      .single();
    if (error) {
      toast({ title: 'Error', description: 'Failed to add tier: ' + error.message, variant: 'destructive' });
      return;
    }
    setTiersByModule((prev) => ({ ...prev, [moduleKey]: [...(prev[moduleKey] || []), data as TierRow] }));
    logPlatformAdminAction('pricing_tier_added', 'pricing_module_tier', moduleKey, { module_key: moduleKey, rate_type: rateType });
  };

  const updateTier = async (tier: TierRow, field: 'tier_min' | 'tier_max' | 'unit_price', value: string) => {
    const parsed = value === '' ? null : Number(value);
    const updated = { ...tier, [field]: field === 'tier_max' ? parsed : (parsed ?? 0) };
    setTiersByModule((prev) => ({
      ...prev,
      [tier.module_key]: (prev[tier.module_key] || []).map((t) => (t.id === tier.id ? updated : t)),
    }));
    const { error } = await supabase
      .from('pricing_module_tiers')
      .update({ [field]: field === 'tier_max' ? parsed : (parsed ?? 0) })
      .eq('id', tier.id);
    if (error) toast({ title: 'Error', description: 'Failed to save tier: ' + error.message, variant: 'destructive' });
  };

  const deleteTier = async (tier: TierRow) => {
    setTiersByModule((prev) => ({
      ...prev,
      [tier.module_key]: (prev[tier.module_key] || []).filter((t) => t.id !== tier.id),
    }));
    const { error } = await supabase.from('pricing_module_tiers').delete().eq('id', tier.id);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete tier: ' + error.message, variant: 'destructive' });
      load();
      return;
    }
    logPlatformAdminAction('pricing_tier_deleted', 'pricing_module_tier', tier.module_key, {
      module_key: tier.module_key,
      tier_min: tier.tier_min,
      tier_max: tier.tier_max,
      unit_price: tier.unit_price,
    });
  };

  const addDiscountTier = async () => {
    const nextMin = discountTiers.length ? Math.max(...discountTiers.map((d) => d.max_modules ?? d.min_modules)) + 1 : 1;
    const { data, error } = await supabase
      .from('pricing_module_count_discounts')
      .insert({ min_modules: nextMin, max_modules: null, discount_percent: 0 })
      .select('*')
      .single();
    if (error) {
      toast({ title: 'Error', description: 'Failed to add discount tier: ' + error.message, variant: 'destructive' });
      return;
    }
    setDiscountTiers((prev) => [...prev, data as DiscountTierRow]);
    logPlatformAdminAction('pricing_discount_tier_added', 'pricing_module_count_discount', String(data.id));
  };

  const updateDiscountTier = async (row: DiscountTierRow, field: 'min_modules' | 'max_modules' | 'discount_percent', value: string) => {
    const parsed = value === '' ? null : Number(value);
    setDiscountTiers((prev) =>
      prev.map((d) => (d.id === row.id ? { ...d, [field]: field === 'max_modules' ? parsed : (parsed ?? 0) } : d))
    );
    const { error } = await supabase
      .from('pricing_module_count_discounts')
      .update({ [field]: field === 'max_modules' ? parsed : (parsed ?? 0) })
      .eq('id', row.id);
    if (error) toast({ title: 'Error', description: 'Failed to save discount tier: ' + error.message, variant: 'destructive' });
  };

  const deleteDiscountTier = async (row: DiscountTierRow) => {
    setDiscountTiers((prev) => prev.filter((d) => d.id !== row.id));
    const { error } = await supabase.from('pricing_module_count_discounts').delete().eq('id', row.id);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete discount tier: ' + error.message, variant: 'destructive' });
      load();
      return;
    }
    logPlatformAdminAction('pricing_discount_tier_deleted', 'pricing_module_count_discount', String(row.id), {
      min_modules: row.min_modules,
      max_modules: row.max_modules,
      discount_percent: row.discount_percent,
    });
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-6">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-foreground flex items-center gap-2">
            <Tag className="h-4 w-4 text-yellow-500" />
            Module rates
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Base is what they pay for this module if it's the only/first one they enable. Module (add-on) is the
            cheaper rate that applies once they've already got at least one other module -- whichever module they
            enabled first is always the one billed at Base.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {modules.map((m) => {
              const Icon = moduleIcon(m.module_key);
              const tiers = tiersByModule[m.module_key] || [];
              const baseTiers = tiers.filter((t) => t.rate_type === 'base');
              const addonTiers = tiers.filter((t) => t.rate_type === 'addon');
              const baseTier = lowestTier(baseTiers);
              const addonTier = lowestTier(addonTiers);
              const unit = m.billing_basis === 'flat' ? '/month' : basisUnit[m.billing_basis];
              return (
                <div
                  key={m.module_key}
                  className="flex flex-col gap-3 p-3.5 rounded-xl border border-yellow-900/30 bg-yellow-900/10"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500">
                      <Icon className="h-4 w-4 text-black" />
                    </div>
                    <p className="text-sm font-medium text-foreground leading-tight">{m.module_name}</p>
                  </div>

                  <Select
                    value={m.billing_basis}
                    onValueChange={(v) => updateBasis(m.module_key, v as ModuleRow['billing_basis'])}
                    disabled={savingBasisKey === m.module_key}
                  >
                    <SelectTrigger className="dark-select h-8 text-xs">
                      <SelectValue>Billed {basisLabel[m.billing_basis]}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-black border-yellow-600">
                      <SelectItem value="per_vehicle">per vehicle</SelectItem>
                      <SelectItem value="per_user">per user</SelectItem>
                      <SelectItem value="flat">flat</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex items-center justify-between gap-2" title="Fixed-cost modules (e.g. IVR) shouldn't be discounted below cost">
                    <Label className="text-[10px] text-muted-foreground leading-tight">Excl. from multi-module discount</Label>
                    <Switch
                      checked={m.excluded_from_module_discount}
                      onCheckedChange={(checked) => toggleExcludedFromDiscount(m.module_key, checked)}
                      disabled={savingBasisKey === m.module_key}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-auto pt-1">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Base</p>
                      {baseTier ? (
                        <p className="text-lg font-bold text-yellow-500 tabular-nums leading-tight">
                          ${baseTier.unit_price.toFixed(2)}
                          <span className="text-[10px] font-normal text-muted-foreground ml-0.5">{unit}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Not set</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">+ Module</p>
                      {addonTier ? (
                        <p className="text-lg font-bold text-orange-400 tabular-nums leading-tight">
                          ${addonTier.unit_price.toFixed(2)}
                          <span className="text-[10px] font-normal text-muted-foreground ml-0.5">{unit}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Not set</p>
                      )}
                    </div>
                  </div>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" className="border-yellow-600 text-yellow-400 hover:bg-yellow-900 h-8 text-xs">
                        <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
                        Rate tiers ({tiers.length})
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96 bg-card border-border" align="start">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-yellow-500" />
                          <p className="text-sm font-medium text-foreground">{m.module_name} rates</p>
                        </div>

                        <div className="space-y-1.5">
                          <p className="text-xs font-medium text-yellow-500">Base -- 1st module</p>
                          <TierEditor
                            tiers={baseTiers}
                            basis={m.billing_basis}
                            onUpdate={updateTier}
                            onDelete={deleteTier}
                            onAdd={() => addTier(m.module_key, 'base')}
                          />
                        </div>

                        <div className="space-y-1.5 pt-2 border-t border-border/60">
                          <p className="text-xs font-medium text-orange-400">Module (add-on) -- 2nd+ module</p>
                          <TierEditor
                            tiers={addonTiers}
                            basis={m.billing_basis}
                            onUpdate={updateTier}
                            onDelete={deleteTier}
                            onAdd={() => addTier(m.module_key, 'addon')}
                          />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-foreground flex items-center gap-2">
            <Percent className="h-4 w-4 text-yellow-500" />
            Multi-module discount
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            An extra discount on top of the Base + Module rates above, applied to a tenant's combined total based on
            how many modules they have enabled in total. Any module with "Excl. from multi-module discount" switched
            on (e.g. IVR, a fixed pass-through cost) is left out of that combined total entirely.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {discountTiers.map((d) => (
              <div
                key={d.id}
                className="flex flex-col gap-2.5 p-3.5 rounded-xl border border-yellow-900/30 bg-yellow-900/10"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-2xl font-bold text-yellow-500 tabular-nums leading-tight">{d.discount_percent}%</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">discount</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteDiscountTier(d)}
                    className="text-red-400 hover:text-red-300 h-7 w-7 p-0 shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Min modules</Label>
                    <Input
                      type="number"
                      value={d.min_modules}
                      onChange={(e) => updateDiscountTier(d, 'min_modules', e.target.value)}
                      className="dark-input h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Max (∞)</Label>
                    <Input
                      type="number"
                      value={d.max_modules ?? ''}
                      placeholder="∞"
                      onChange={(e) => updateDiscountTier(d, 'max_modules', e.target.value)}
                      className="dark-input h-8 text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Discount (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={d.discount_percent}
                    onChange={(e) => updateDiscountTier(d, 'discount_percent', e.target.value)}
                    className="dark-input h-8 text-xs"
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addDiscountTier}
              className="flex flex-col items-center justify-center gap-1.5 p-3.5 rounded-xl border border-dashed border-yellow-900/40 text-yellow-500 hover:bg-yellow-900/10 transition-colors min-h-[136px]"
            >
              <Plus className="w-5 h-5" />
              <span className="text-xs font-medium">Add tier</span>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminPricing;
